import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { SortByShape, isSortByShape, useSortData } from '../../hooks/use-sort-data'
import { SortableValue } from '../../types'
import { SortByColumnOptions } from '../../util'

export enum SortDirection {
  Ascending = 1,
  Descending = -1,
}

export interface TableSort<ColumnType> {
  column: ColumnType
  direction: SortDirection
}

/**
 * Default sort getter which either gets column matching by `defaultSortId` or returns the first column
 */
export function getDefaultSort<ColumnType, DefaultSort>(
  columns: ColumnType[],
  defaultSort?: DefaultSort
): TableSort<DefaultSort extends null ? ColumnType | null : ColumnType> {
  if (defaultSort === null) return { column: null as ColumnType, direction: SortDirection.Ascending }
  if (defaultSort && typeof defaultSort === 'object') return defaultSort as unknown as TableSort<ColumnType>

  const foundColumn = columns.find((column) => isSortByShape(column) && column.id === defaultSort)

  return {
    column: foundColumn ?? (defaultSort as unknown as ColumnType | undefined) ?? columns[0],
    direction: SortDirection.Ascending,
  }
}

/**
 * Get default sorting from url when persisting sort
 */
export function getDefaultSortFromUrl<ColumnType extends SortByShape<any, any>, OrderType extends string | null>(
  columns: ColumnType[],
  order?: OrderType,
  idKey: keyof ColumnType = 'id'
): TableSort<OrderType extends null ? ColumnType | null : ColumnType> {
  if (order === null) return { column: null as unknown as ColumnType, direction: SortDirection.Ascending }

  const isDesc = order?.startsWith('-')
  const queryParam = isDesc ? order?.substring(1) : order
  const column = columns.find((column) => column[idKey] === queryParam)

  if (column) {
    return { column, direction: isDesc ? SortDirection.Descending : SortDirection.Ascending }
  } else {
    return { column: columns[0], direction: SortDirection.Ascending }
  }
}

export function saveSortToUrl<ColumnType extends SortByShape<any, any>>(
  prev: URLSearchParams,
  urlPersistKey: string,
  newSort: ColumnType | null,
  idKey: keyof ColumnType = 'id',
  defaultSort: unknown = null
) {
  const newParams = new URLSearchParams(prev)
  const previousOrder = newParams.get(urlPersistKey) ?? defaultSort
  const isDescOrder = previousOrder?.toString().startsWith('-')

  if (newSort === null) {
    newParams.delete(urlPersistKey)
  } else {
    newParams.set(urlPersistKey, `${!isDescOrder && previousOrder === newSort[idKey] ? '-' : ''}${newSort[idKey]}`)
  }

  return newParams
}

interface UseTableSortProps<
  IdType,
  DataType,
  ColumnType extends SortByShape<IdType, any>,
  DefaultSort extends ColumnType['id'] | TableSort<ColumnType> | null,
> {
  data: DataType[]
  columns: ColumnType[]

  /**
   * You can either provide id of the column or whole shape
   * defaultSort: { column: columns[0], direction: -1 }
   * If your ids are constants (or enums), you will get name suggestions
   */
  defaultSort?: DefaultSort

  /**
   * Custom value getter. By default these can be defined on every column using `getValueForSort`
   */
  valueGetter?: (column: ColumnType) => (row: DataType) => SortableValue

  /**
   * Additional options for sorting, including ignoring null values or sorting null as last
   */
  options?: SortByColumnOptions
}

/**
 * This hook has multiple
 */
export function useTableSort<
  IdType,
  DataType,
  ColumnType extends SortByShape<IdType, any>,
  DefaultSort extends ColumnType['id'] | TableSort<ColumnType> | null,
  SortType extends DefaultSort extends null ? ColumnType | null : ColumnType,
>({ data, columns, defaultSort, valueGetter, options }: UseTableSortProps<IdType, DataType, ColumnType, DefaultSort>) {
  const [sort, setSort] = React.useState(getDefaultSort(columns, defaultSort) as TableSort<SortType>)

  const changeSort = React.useCallback((newSort: SortType, direction?: SortDirection) => {
    setSort((prevSort) => {
      if (newSort && direction) {
        return { column: newSort, direction }
      }

      if (newSort && prevSort.column?.id === newSort.id) {
        return { ...prevSort, direction: prevSort.direction * -1 }
      }

      return { column: newSort, direction: SortDirection.Ascending }
    })
  }, [])

  const sortedData = useSortData({ data, direction: sort.direction, sortBy: sort.column, valueGetter, options })

  return {
    data: sortedData,
    sort,
    changeSort,
  }
}

interface UseTableSortWithUrlProps<
  IdType,
  DataType,
  ColumnType extends SortByShape<IdType, any>,
  DefaultSort extends ColumnType['id'] | TableSort<ColumnType> | null,
> extends UseTableSortProps<IdType, DataType, ColumnType, DefaultSort> {
  /**
   * When set, the sorting will be persisted in the URL. Loading the
   *
   * @default undefined
   */
  urlPersistKey?: string

  /**
   * Key to get value of the URL sort from a column
   * @deafult "id"
   */
  idKey?: keyof ColumnType
}

export function useTableSortWithUrl<
  IdType,
  DataType,
  ColumnType extends SortByShape<IdType, any>,
  DefaultSort extends ColumnType['id'] | TableSort<ColumnType> | null,
  SortType extends DefaultSort extends null ? ColumnType | null : ColumnType,
>({
  data,
  columns,
  defaultSort,
  valueGetter,
  urlPersistKey,
  idKey = 'id',
  options,
}: UseTableSortWithUrlProps<IdType, DataType, ColumnType, DefaultSort>) {
  const {
    data: sortedData,
    changeSort: changeSortState,
    sort,
  } = useTableSort({ data, columns, defaultSort, valueGetter, options })
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultSortRef = React.useRef(
    urlPersistKey && typeof defaultSort === 'string'
      ? getDefaultSortFromUrl(columns, searchParams.get(urlPersistKey) ?? defaultSort, idKey)
      : getDefaultSort(columns, defaultSort)
  )

  const changeSort = React.useCallback(
    (newSort: SortType) => {
      if (urlPersistKey) {
        const defaultSort: ColumnType | null = defaultSortRef.current.column
        setSearchParams(
          (prev) =>
            saveSortToUrl(prev, urlPersistKey, newSort, idKey, defaultSort === null ? null : defaultSort[idKey]),
          {
            preventScrollReset: true,
          }
        )
      }

      changeSortState(newSort)
    },
    [idKey, setSearchParams, urlPersistKey, changeSortState]
  )

  /**
   * Update sort from url
   */
  React.useEffect(() => {
    if (!urlPersistKey) return

    const newSort = searchParams.get(urlPersistKey)
    if (newSort) {
      const sort = getDefaultSortFromUrl(columns, newSort, idKey)
      changeSortState(sort.column, sort.direction)
    }
  }, [columns, idKey, searchParams, urlPersistKey, changeSortState])

  return {
    data: sortedData,
    sort,
    changeSort,
  }
}
